// src/private/src/pages/app/clients/ClientDetail.jsx
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  FormControlLabel,
  MenuItem,
  TextField,
  Card,
  CardHeader,
  CardContent,
  Fade,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from "@src/api/clients";
import InputAdornment from '@mui/material/InputAdornment';


const fieldGroups = [
    {
        title: "Client Details",
        subgroups: [
          {
            subtitle: "General",
            fields: [
              { name: "name", label: "Client Name", type: "text" },
              { name: "active", label: "Active", type: "boolean" },
              { name: "client_logo_url", label: "Client Logo URL", type: "url" },
              { name: "Vertical", label: "Vertical", type: "vertical-select" },
              { name: "skip_webhook", label: "Skip Webhook", type: "boolean" },
              { name: "skip_lead_update_task", label: "Skip Lead Update Task", type: "boolean" },
              { name: "skip_out_of_area_leads", label: "Skip Out-of-Area Leads", type: "boolean" },
              { name: "fees", label: "Fees", type: "number" },
              { name: "goal_number", label: "Goal Number", type: "number" },
              { name: "self_book_calendar", label: "Self Book Calendar", type: "url" },
              { name: "test_zip", label: "Test Zip", type: "text" },
              { name: "test_address", label: "Test Address", type: "text" },
              { name: "current_offer", label: "Current Offer", type: "text" },
            ]
          },
          {
            subtitle: "Google Spreadsheet Configuration",
            fields: [
              { name: "google_spreadsheet_url", label: "Google Spreadsheet URL", type: "url" },
              { name: "spreadsheet_fields", label: "Spreadsheet Fields", type: "text" },
            ]
          }
        ]
      },
  {
    title: "Lead Handling",
    subgroups: [
      {
        subtitle: "General",
        fields: [
          { name: "phone_format", label: "Phone Format", type: "select", options: [
            { value: "PLAIN", label: "8584728910" },
            { value: "INTERNATIONAL", label: "+18584728910" },
            { value: "(NANP)", label: "858-472-8910" },
            { value: "US_FORMAT", label: "(858) 472-8910" },
          ]
     },
          { name: "regular_lead_email", label: "Regular Lead Email", type: "boolean" },
          { name: "self_book_email", label: "Self Book Email", type: "boolean" },
          { name: "renter_email", label: "Renter Email", type: "boolean" },
          { name: "email_recipients", label: "Email Recipients", type: "text" },
        ]
      },
      {
        subtitle: "Primary Webhook",
        fields: [
          { name: "webhook", label: "Webhook", type: "text" },
          { name: "send_as_form_encoded", label: "Send as Form Encoded", type: "boolean" },
          { name: "success_post_keyword", label: "Success Post Keyword", type: "text" },
          { name: "custom_headers", label: "Custom Headers", type: "json" },
          { name: "payload_mapping", label: "Payload Mapping", type: "json" },
          { name: "comments_key", label: "Comments Key", type: "text" },
          { name: "comments_value", label: "Comments Value", type: "text" },
        ]
      },
      {
        subtitle: "Self-Book Webhook",
        fields: [
          { name: "webhook_selfbook", label: "Webhook Selfbook", type: "text" },
          { name: "self_book_send_as_form_encoded", label: "Self Book Send as Form Encoded", type: "boolean" },
          { name: "self_book_custom_headers", label: "Self Book Custom Headers", type: "json" },
          { name: "self_book_payload_mapping", label: "Self Book Payload Mapping", type: "json" },
          { name: "self_book_success_keyword", label: "Self Book Success Keyword", type: "text" },
        ]
      },
      {
        subtitle: "Secondary Webhook",
        fields: [
          { name: "secondary_webhook", label: "Secondary Webhook", type: "text" },
          { name: "secondary_send_as_form_encoded", label: "Secondary Send as Form Encoded", type: "boolean" },
          { name: "secondary_custom_headers", label: "Secondary Custom Headers", type: "json" },
          { name: "secondary_payload_mapping", label: "Secondary Payload Mapping", type: "json" },
        ]
      },
      {
        subtitle: "Secondary Self-Book Webhook",
        fields: [
          { name: "secondary_webhook_selfbook", label: "Secondary Webhook Selfbook", type: "text" },
          { name: "secondary_self_book_send_as_form_encoded", label: "Secondary Self Book Send as Form Encoded", type: "boolean" },
          { name: "secondary_self_book_custom_headers", label: "Secondary Self Book Custom Headers", type: "json" },
          { name: "secondary_self_book_payload_mapping", label: "Secondary Self Book Payload Mapping", type: "json" },
        ]
      }
    ]
  },
  {
    title: "Wiki",
    fields: [
      { name: "notes", label: "Client Wiki", type: "markdown" },
    ],
  },
  {
    title: "Budget",
    fields: [
      { name: "budget_name", label: "Budget Name", type: "text" },
      { name: "budget_amount", label: "Budget Amount", type: "number" },
      { name: "budget_start_date", label: "Start Date", type: "date" },
      { name: "budget_end_date", label: "End Date", type: "date" },
    ]
  },
  {
    title: "Zips",
    fields: []  // No form fields, just a table view
  },
  {
    title: "POCs",
    fields: []  // Display only a table, no input fields in this view
  },
  {
    title: "Zip Filters",
    fields: []  // used just to activate the section
  },
  
];

export default function ClientDetailPage() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const isNew = clientId === 'new';
  
    const [client, setClient] = useState(null);
    const [formData, setFormData] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [selectedGroup, setSelectedGroup] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [notesEditMode, setNotesEditMode] = useState(isNew);
    const [loading, setLoading] = useState(!isNew);
  
    const [budgets, setBudgets] = useState([]);
    const [newBudget, setNewBudget] = useState({
      date: "",
      vertical: "",
      location: "",
      channel: "",
      amount: ""
    });
  
    const [verticalOptions, setVerticalOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [channelOptions, setChannelOptions] = useState([]);
    const [clientZips, setClientZips] = useState([]);
    const [pocs, setPocs] = useState([]);
    const [zipFilters, setZipFilters] = useState([]);

    const [newZipFilter, setNewZipFilter] = useState({
        vertical: "",
        min_household_income: "60000",
        max_household_income: "250000",
        min_home_value: "200000",
        max_home_value: "2500000",
        min_home_ownership_percentage: "30",
        min_sfh_percentage: "30",
        min_home_age_percentage: "0"
      });
      
    
    const [newPoc, setNewPoc] = useState({
        first_name: "",
        last_name: "",
        email: "",
        position: "",
        role: ""
      });
      
    

  
    
    useEffect(() => {
      if (!isNew) {
        api.request({ url: `dashboard/clients/${clientId}/`, method: "GET" })
          .then((res) => {
            setClient(res.data);
            setFormData(res.data);
            setOriginalData(res.data);
          })
          .catch((err) => console.error("Error fetching client:", err))
          .finally(() => setLoading(false));
      }
    }, [clientId]);
  
    useEffect(() => {
      if (!isNew) {
        api.request({ url: `dashboard/client-budgets/?client_id=${clientId}`, method: "GET" })
          .then((res) => setBudgets(res.data))
          .catch((err) => console.error("Error fetching budgets:", err));
  
        api.request({ url: `dashboard/verticals/`, method: "GET" })
          .then(res => setVerticalOptions(res.data.map(opt => ({ value: opt.id, label: opt.vertical }))));
        api.request({ url: `dashboard/client-territories/?client_id=${clientId}`, method: "GET" })
          .then(res => setLocationOptions(res.data.map(opt => ({ value: opt.id, label: opt.name }))));
        api.request({ url: `dashboard/channels/`, method: "GET" })
          .then(res => setChannelOptions(res.data.map(opt => ({ value: opt.id, label: opt.name }))));
        api.request({ url: `dashboard/client-zips/?client_id=${clientId}`, method: "GET" })
          .then(res => setClientZips(res.data))
          .catch(err => console.error("Error fetching client zips:", err));
        api.request({ url: `dashboard/client-pocs/?client_id=${clientId}`, method: "GET" })
          .then(res => setPocs(res.data))
          .catch(err => console.error("Error fetching client POCs:", err));
        api.request({ url: `dashboard/client-zip-filters/?client_id=${clientId}`, method: "GET" })
          .then(res => setZipFilters(res.data))
          .catch(err => console.error("Error fetching client zip filters:", err));
        
    
      }
    }, [clientId]);
  
    const handleChange = (name, value) => {
      setFormData(prev => ({ ...prev, [name]: value }));
    };
  
    const handleBudgetChange = (field, value) => {
      setNewBudget(prev => ({ ...prev, [field]: value }));
    };

    const handlePocChange = (field, value) => {
        setNewPoc(prev => ({ ...prev, [field]: value }));
      };

    
      
  
    const addBudget = () => {
      const payload = { ...newBudget, client: clientId };
      api.request({ url: `dashboard/client-budgets/`, method: "POST", payload })
        .then(() => {
          setNewBudget({ date: "", vertical: "", location: "", channel: "", amount: "" });
          return api.request({ url: `dashboard/client-budgets/?client_id=${clientId}`, method: "GET" });
        })
        .then(res => setBudgets(res.data))
        .catch(err => alert("Failed to add budget."));
    };
  
    const handleCancel = () => {
      setFormData(originalData);
    };
  
    const handleZipFilterChange = (field, value) => {
        setNewZipFilter(prev => ({ ...prev, [field]: value }));
      };
      
      const addZipFilter = () => {
        const payload = { ...newZipFilter, client: clientId };
        api.request({ url: `dashboard/client-zip-filters/`, method: "POST", payload })
          .then(() => {
            setNewZipFilter({
              vertical: "",
              min_household_income: "",
              max_household_income: "",
              min_home_value: "",
              max_home_value: "",
              min_home_ownership_percentage: "",
              min_sfh_percentage: "",
              min_home_age_percentage: ""
            });
            return api.request({ url: `dashboard/client-zip-filters/?client_id=${clientId}`, method: "GET" });
          })
          .then(res => setZipFilters(res.data))
          .catch(err => alert("Failed to add zip filter."));
      };
     
      const addPoc = () => {
        const payload = { ...newPoc, client: clientId };
        api.request({ url: `dashboard/client-pocs/`, method: "POST", payload })
          .then(() => {
            setNewPoc({ first_name: "", last_name: "", email: "", position: "", role: "" });
            return api.request({ url: `dashboard/client-pocs/?client_id=${clientId}`, method: "GET" });
          })
          .then(res => setPocs(res.data))
          .catch(err => alert("Failed to add POC."));
      };
      



    const handleSubmit = async () => {
      setIsSaving(true);
      try {
        if (isNew) {
          const res = await api.request({ url: `dashboard/clients/`, method: "POST", payload: formData });
          navigate(`/clients/${res.data.id}/`);
        } else {
          await api.request({ url: `dashboard/clients/${clientId}/`, method: "PUT", payload: formData });
          alert("Client updated successfully");
        }
      } catch {
        alert("Failed to save client");
      } finally {
        setIsSaving(false);
      }
    };
  
    const currentGroup = fieldGroups[selectedGroup];
    if (!isNew && loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  
    return (
      <Box display="flex" px={3} py={4}>
        <Paper elevation={0} sx={{ width: 220, mr: 4, p: 2, backgroundColor: '#F9FAFB', borderRadius: 2 }}>
          <List>
            {fieldGroups.map((group, idx) => (
              <ListItemButton
                key={group.title}
                selected={selectedGroup === idx}
                onClick={() => setSelectedGroup(idx)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText primary={group.title} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
  
        <Fade in>
          <Box flex={1}>
            <Box display="flex" alignItems="center" mb={2}>
              <Button onClick={() => navigate('/clients')} startIcon={<ArrowBackIcon />} size="small" sx={{ textTransform: 'none' }}>
                Back
              </Button>
            </Box>
  
            {currentGroup.title === "Budget" ? (
              <Card>
              <CardHeader title="Budgets" />
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Vertical</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* ✅ Input Row FIRST */}
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          value={newBudget.date}
                          onChange={(e) => handleBudgetChange("date", e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={newBudget.vertical}
                          onChange={(e) => handleBudgetChange("vertical", e.target.value)}
                          fullWidth
                        >
                          {verticalOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={newBudget.location}
                          onChange={(e) => handleBudgetChange("location", e.target.value)}
                          fullWidth
                        >
                          {locationOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={newBudget.channel}
                          onChange={(e) => handleBudgetChange("channel", e.target.value)}
                          fullWidth
                        >
                          {channelOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={newBudget.amount}
                          onChange={(e) => handleBudgetChange("amount", e.target.value)}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </TableCell>
                    </TableRow>
            
                    {/* ✅ Existing Budget Rows BELOW */}
                    {budgets.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>
                          {b.date
                            ? new Date(b.date).toLocaleString("default", {
                                month: "long",
                              })
                            : "-"}
                        </TableCell>
                        <TableCell>{b.date}</TableCell>
                        <TableCell>{b.vertical_data?.vertical || "-"}</TableCell>
                        <TableCell>{b.location_data?.name || "-"}</TableCell>
                        <TableCell>{b.channel_data?.name || "-"}</TableCell>
                        <TableCell>${parseFloat(b.amount).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            
                {/* ✅ Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() =>
                      setNewBudget({
                        date: "",
                        vertical: "",
                        location: "",
                        channel: "",
                        amount: "",
                      })
                    }
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={addBudget}>
                    + Add Budget
                  </Button>
                </Stack>
              </CardContent>
            </Card>
            
            ) : currentGroup.title === "Zips" ? (
                <Card>
                  <CardHeader title="Client Zips" />
                  <CardContent>
                    <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                        <TableRow>
                            <TableCell>Client</TableCell>
                            <TableCell>ZIP</TableCell>
                            <TableCell>Vertical</TableCell>
                            <TableCell>Territory</TableCell>
                            <TableCell>Calendar</TableCell>
                            <TableCell>Active</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {clientZips.map(zip => (
                            <TableRow key={zip.id}>
                            <TableCell>{zip.client_name}</TableCell>
                            <TableCell>{zip.zip}</TableCell>
                            <TableCell>{zip.vertical_name}</TableCell>
                            <TableCell>{zip.territory_name || '-'}</TableCell>
                            <TableCell>{zip.calendar || '-'}</TableCell>
                            <TableCell>{zip.active ? '✅' : '❌'}</TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
                    ) : currentGroup.title === "POCs" ? (
                        <Card>
                        <CardHeader title="Client Points of Contact" />
                        <CardContent>
                            <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                <TableRow>
                                    <TableCell>First Name</TableCell>
                                    <TableCell>Last Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Role</TableCell>
                                </TableRow>
                                </TableHead>
                                <TableBody>
                                {/* ✅ Form Row FIRST */}
                                <TableRow>
                                    <TableCell>
                                    <TextField
                                        value={newPoc.first_name}
                                        onChange={(e) => handlePocChange("first_name", e.target.value)}
                                        fullWidth
                                    />
                                    </TableCell>
                                    <TableCell>
                                    <TextField
                                        value={newPoc.last_name}
                                        onChange={(e) => handlePocChange("last_name", e.target.value)}
                                        fullWidth
                                    />
                                    </TableCell>
                                    <TableCell>
                                    <TextField
                                        value={newPoc.email}
                                        onChange={(e) => handlePocChange("email", e.target.value)}
                                        fullWidth
                                    />
                                    </TableCell>
                                    <TableCell>
                                    <TextField
                                        value={newPoc.position}
                                        onChange={(e) => handlePocChange("position", e.target.value)}
                                        fullWidth
                                    />
                                    </TableCell>
                                    <TableCell>
                                    <TextField
                                        select
                                        value={newPoc.role}
                                        onChange={(e) => handlePocChange("role", e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value="main">Main</MenuItem>
                                        <MenuItem value="secondary">Secondary</MenuItem>
                                        <MenuItem value="billing">Billing</MenuItem>
                                        <MenuItem value="technical">Technical</MenuItem>
                                    </TextField>
                                    </TableCell>
                                </TableRow>

                                {/* ✅ Existing Records BELOW */}
                                {pocs.map((poc) => (
                                    <TableRow key={poc.id}>
                                    <TableCell>{poc.first_name}</TableCell>
                                    <TableCell>{poc.last_name}</TableCell>
                                    <TableCell>{poc.email}</TableCell>
                                    <TableCell>{poc.position}</TableCell>
                                    <TableCell>{poc.role}</TableCell>
                                    </TableRow>
                                ))}
                                </TableBody>
                            </Table>
                            </TableContainer>

                            {/* ✅ Buttons */}
                            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() =>
                                setNewPoc({
                                    first_name: "",
                                    last_name: "",
                                    email: "",
                                    position: "",
                                    role: "",
                                })
                                }
                            >
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={addPoc}>
                                + Add POC
                            </Button>
                            </Stack>
                        </CardContent>
                        </Card>

                ) : currentGroup.title === "Zip Filters" ? (
                    <Card>
                        <CardHeader title="Client Zip Filters" />
                        <CardContent>
                            <Table>
                            <TableHead>
                                <TableRow>
                                <TableCell>Vertical</TableCell>
                                <TableCell>HHI Min</TableCell>
                                <TableCell>HHI Max</TableCell>
                                <TableCell>Home Value Min</TableCell>
                                <TableCell>Home Value Max</TableCell>
                                <TableCell>Ownership %</TableCell>
                                <TableCell>SFH %</TableCell>
                                <TableCell>Age %</TableCell>
                                <TableCell>Created</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* ✅ Input Row FIRST */}
                                <TableRow>
                                <TableCell>
                                    <Box minWidth={160}>
                                    <TextField
                                        select
                                        value={newZipFilter.vertical}
                                        onChange={(e) => handleZipFilterChange("vertical", e.target.value)}
                                        fullWidth
                                    >
                                        {verticalOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                        ))}
                                    </TextField>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.min_household_income}
                                    onChange={(e) => handleZipFilterChange("min_household_income", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.max_household_income}
                                    onChange={(e) => handleZipFilterChange("max_household_income", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.min_home_value}
                                    onChange={(e) => handleZipFilterChange("min_home_value", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.max_home_value}
                                    onChange={(e) => handleZipFilterChange("max_home_value", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.min_home_ownership_percentage}
                                    onChange={(e) => handleZipFilterChange("min_home_ownership_percentage", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.min_sfh_percentage}
                                    onChange={(e) => handleZipFilterChange("min_sfh_percentage", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                    type="number"
                                    value={newZipFilter.min_home_age_percentage}
                                    onChange={(e) => handleZipFilterChange("min_home_age_percentage", e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                    />
                                </TableCell>
                                <TableCell>-</TableCell>
                                </TableRow>

                                {/* ✅ Existing Records */}
                                {zipFilters.map((z) => (
                                <TableRow key={z.id}>
                                    <TableCell>
                                    {
                                        verticalOptions.find((opt) => opt.value === z.vertical)?.label ||
                                        z.vertical ||
                                        "-"
                                    }
                                    </TableCell>
                                    <TableCell>{z.min_household_income ? `$${parseInt(z.min_household_income).toLocaleString()}` : "-"}</TableCell>
                                    <TableCell>{z.max_household_income ? `$${parseInt(z.max_household_income).toLocaleString()}` : "-"}</TableCell>
                                    <TableCell>{z.min_home_value ? `$${parseInt(z.min_home_value).toLocaleString()}` : "-"}</TableCell>
                                    <TableCell>{z.max_home_value ? `$${parseInt(z.max_home_value).toLocaleString()}` : "-"}</TableCell>
                                    <TableCell>{z.min_home_ownership_percentage ? `${parseFloat(z.min_home_ownership_percentage)}%` : "-"}</TableCell>
                                    <TableCell>{z.min_sfh_percentage ? `${parseFloat(z.min_sfh_percentage)}%` : "-"}</TableCell>
                                    <TableCell>{z.min_home_age_percentage ? `${parseFloat(z.min_home_age_percentage)}%` : "-"}</TableCell>
                                    <TableCell>{z.created_at?.slice(0, 10) || "-"}</TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                            </Table>

                            {/* ✅ Buttons */}
                            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() =>
                                setNewZipFilter({
                                    vertical: "",
                                    min_household_income: "",
                                    max_household_income: "",
                                    min_home_value: "",
                                    max_home_value: "",
                                    min_home_ownership_percentage: "",
                                    min_sfh_percentage: "",
                                    min_home_age_percentage: "",
                                })
                                }
                            >
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={addZipFilter}>
                                + Add Filter
                            </Button>
                            </Stack>
                        </CardContent>
                    </Card>

                  
                        
              ) : (
              <Card elevation={1}>
            
            
                <CardHeader title={<Typography variant="h6">{currentGroup.title}</Typography>} />
                <CardContent>
            {currentGroup.subgroups ? (
                <>
                {currentGroup.subgroups.map((subgroup, idx) => (
                    <Card key={idx} variant="outlined" sx={{ mb: 3 }}>
                    <CardHeader
                        title={<Typography variant="subtitle1">{subgroup.subtitle}</Typography>}
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        <Grid container spacing={3}>
                        {subgroup.fields.map(({ name, label, type, disabled, options }) => (
                            <Grid item xs={12} key={name}>
                            {type === "boolean" ? (
                                <FormControlLabel
                                control={
                                    <Switch
                                    checked={!!formData[name]}
                                    onChange={(e) => handleChange(name, e.target.checked)}
                                    disabled={disabled}
                                    />
                                }
                                label={label}
                                />
                            ) : type === "select" ? (
                                <TextField
                                select
                                label={label}
                                value={formData[name] || ""}
                                onChange={(e) => handleChange(name, e.target.value)}
                                fullWidth
                                disabled={disabled}
                                >
                                {options?.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                    </MenuItem>
                                ))}
                                </TextField>
                            ) : type === "json" ? (
                                <TextField
                                label={label}
                                name={name}
                                value={
                                    typeof formData[name] === "object"
                                    ? JSON.stringify(formData[name], null, 2)
                                    : formData[name] || ""
                                }
                                onChange={(e) => handleChange(name, e.target.value)}
                                fullWidth
                                multiline
                                minRows={6}
                                sx={{ fontFamily: "monospace" }}
                                helperText="Enter valid JSON format"
                                disabled={disabled}
                                />
                            ) : type === "markdown" ? (
                                <Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">{label}</Typography>
                                    {!isNew && (
                                    <Button size="small" onClick={() => setNotesEditMode(!notesEditMode)}>
                                        {notesEditMode ? "Preview" : "Edit"}
                                    </Button>
                                    )}
                                </Box>
                                {notesEditMode ? (
                                    <ReactQuill
                                    theme="snow"
                                    value={formData[name] || ""}
                                    onChange={(value) => handleChange(name, value)}
                                    style={{ height: 800, marginBottom: 40 }}
                                    />
                                ) : (
                                    <Paper variant="outlined" sx={{ p: 2, minHeight: 600 }}>
                                    <div
                                        dangerouslySetInnerHTML={{
                                        __html: formData[name] || "<i>No notes yet.</i>",
                                        }}
                                    />
                                    </Paper>
                                )}
                                </Box>
                            ) : (
                                <TextField
                                label={label}
                                name={name}
                                value={formData[name] || ""}
                                onChange={(e) => handleChange(name, e.target.value)}
                                fullWidth
                                disabled={disabled}
                                type={type || "text"}
                                />
                            )}
                            </Grid>
                        ))}
                        </Grid>
                    </CardContent>
        </Card>
      ))}
    </>
  ) : (
    <Grid container spacing={3}>
      {currentGroup.fields.map(({ name, label, type, disabled, options }) => (
        <Grid item xs={12} key={name}>
          {type === "boolean" ? (
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData[name]}
                  onChange={(e) => handleChange(name, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={label}
            />
          ) : type === "select" ? (
            <TextField
              select
              label={label}
              value={formData[name] || ""}
              onChange={(e) => handleChange(name, e.target.value)}
              fullWidth
              disabled={disabled}
            >
              {options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          ) : type === "json" ? (
            <TextField
              label={label}
              name={name}
              value={
                typeof formData[name] === "object"
                  ? JSON.stringify(formData[name], null, 2)
                  : formData[name] || ""
              }
              onChange={(e) => handleChange(name, e.target.value)}
              fullWidth
              multiline
              minRows={6}
              sx={{ fontFamily: "monospace" }}
              helperText="Enter valid JSON format"
              disabled={disabled}
            />
          ) : type === "markdown" ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">{label}</Typography>
                {!isNew && (
                  <Button size="small" onClick={() => setNotesEditMode(!notesEditMode)}>
                    {notesEditMode ? "Preview" : "Edit"}
                  </Button>
                )}
              </Box>
              {notesEditMode ? (
                <ReactQuill
                  theme="snow"
                  value={formData[name] || ""}
                  onChange={(value) => handleChange(name, value)}
                  style={{ height: 800, marginBottom: 40 }}
                />
              ) : (
                <Paper variant="outlined" sx={{ p: 2, minHeight: 600 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formData[name] || "<i>No notes yet.</i>",
                    }}
                  />
                </Paper>
              )}
            </Box>
          ) : (
            <TextField
              label={label}
              name={name}
              value={formData[name] || ""}
              onChange={(e) => handleChange(name, e.target.value)}
              fullWidth
              disabled={disabled}
              type={type || "text"}
            />
          )}
        </Grid>
      ))}
    </Grid>
  )}

  <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
    <Button onClick={handleCancel} disabled={isSaving}>
      Cancel
    </Button>
    <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSaving}>
      {isSaving ? "Saving..." : isNew ? "Create Client" : "Save Changes"}
    </Button>
  </Box>
</CardContent>

              </Card>
            )}
          </Box>
        </Fade>
      </Box>
    );
  }